import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonCard } from '../../../../Components/Common/Skeleton';
import { getAuthToken } from '../../../../auth/tokenvalidation';
import {
  isLpgProduct,
  usesDyedUndyedAlpha,
  getAlphaColumnLabel,
  getProcurementProductNameId,
  getProcurementProductTypeId,
  fetchProductTypePricingUnit,
} from '../../../TenderOpening/utils/tenderOpeningHelpers';
import HpsParcelNonLpg from './nonLpg/HpsParcelNonLpg';
import HpsParcelLpg from './lpg/HpsParcelLpg';
import {
  getCargoProductTypeName,
  hasMultipleProductTypes,
  requiresLpgCargoRatioForCargo,
} from '../../../../shared/offer-domain/constants';

const HpsParcelSection = ({
  parcels = [],
  isLoadingParcels = false,
  procurement,
  isReadOnly = false,
  isAlphaEditable = false,
  onCargoRatioStatusChange,
  cargoRatioErrorParcelId = null,
  onClearCargoRatioError,
}) => {
  const [pricingUnit, setPricingUnit] = useState(null);
  const isLpg = isLpgProduct(procurement);
  const productNameId = getProcurementProductNameId(procurement);
  const usesDyedUndyed = usesDyedUndyedAlpha(productNameId);
  const quantityUnitLabel =
    procurement?.productType?.unit || (isLpg ? 'MT' : 'MB');

  useEffect(() => {
    if (isLpg || usesDyedUndyed) {
      setPricingUnit(null);
      return;
    }
    const productTypeId = getProcurementProductTypeId(procurement);
    const token = getAuthToken();
    let cancelled = false;
    fetchProductTypePricingUnit(productTypeId, token).then(unit => {
      if (!cancelled) setPricingUnit(unit);
    });
    return () => {
      cancelled = true;
    };
  }, [isLpg, usesDyedUndyed, procurement]);

  const displayParcels = useMemo(() => {
    const showProductType = hasMultipleProductTypes(procurement);
    return parcels.map(parcel => ({
      ...parcel,
      productTypeName: showProductType
        ? getCargoProductTypeName(procurement, parcel)
        : null,
    }));
  }, [parcels, procurement]);

  if (isLoadingParcels) {
    return <SkeletonCard lines={3} />;
  }

  if (!parcels.length) {
    return <p className='text-muted small mb-0'>No parcel data available.</p>;
  }

  if (isLpg) {
    return (
      <HpsParcelLpg
        parcels={displayParcels}
        procurementId={procurement?.id}
        quantityUnitLabel={quantityUnitLabel}
        getRequiresCargoRatio={parcel =>
          requiresLpgCargoRatioForCargo(procurement, parcel)
        }
        isReadOnly={isReadOnly}
        isAlphaEditable={isAlphaEditable}
        onCargoRatioStatusChange={onCargoRatioStatusChange}
        cargoRatioErrorParcelId={cargoRatioErrorParcelId}
        onClearCargoRatioError={onClearCargoRatioError}
      />
    );
  }

  return (
    <HpsParcelNonLpg
      parcels={displayParcels}
      procurementId={procurement?.id}
      quantityUnitLabel={quantityUnitLabel}
      usesDyedUndyed={usesDyedUndyed}
      alphaColumnLabel={getAlphaColumnLabel(pricingUnit)}
    />
  );
};

export default HpsParcelSection;
